import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppBridge } from '@shopify/app-bridge-react';
import { API_BASE, SHOP_DOMAIN } from './constants';
import { useI18n } from './i18n';
import BrandLoading from './components/BrandLoading';

// Shopify 打开嵌入应用时拼在 URL 上的参数:iframe 里始终有 shop + host,
// 本地直接开 localhost:3000 时两者皆无 —— 以此区分嵌入/本地两种模式。
export function shopifyUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop');
  const host = params.get('host');
  return { embedded: Boolean(shop && host), shop, host };
}

// App Bridge 的某些整页跳转(如详情页的返回箭头)会把 iframe 重载到不带
// ?shop=&host= 的地址,应用会因此误判为本地模式,所有请求无令牌(400)。
// 对策:嵌入时把参数记进 sessionStorage;启动时发现 URL 丢参但记忆里有,
// 就补全参数并重定向一次 —— App Bridge CDN 脚本需要 URL 上有这两个参数
// 才能与宿主建立会话。sessionStorage 按标签页隔离,本地裸开不受影响。
const EMBED_CONTEXT_KEY = 'pp_embed_context';

export function saveEmbedContext(shop, host) {
  try {
    sessionStorage.setItem(EMBED_CONTEXT_KEY, JSON.stringify({ shop, host }));
  } catch {
    // 隐私模式等场景拿不到 sessionStorage —— 退化回原来的行为
  }
}

// 返回需要补参的上下文;null = 不需要(参数齐全,或确实是从未嵌入过的本地模式)。
export function embedContextToRestore() {
  const { embedded } = shopifyUrlParams();
  if (embedded) return null;
  try {
    const saved = JSON.parse(sessionStorage.getItem(EMBED_CONTEXT_KEY) || 'null');
    if (saved && saved.shop && saved.host) return saved;
  } catch {
    // ignore
  }
  return null;
}

// AuthContext 值:
//   shopDomain      — 真实店铺域名(嵌入)或 demo-shop.myshopify.com(本地)
//   getSessionToken — 返回 App Bridge session token 的函数;null = 本地模式,
//                     请求不带 Authorization 头
//   ensureInstall   — 懒执行的安装(token exchange);null = 本地模式
//   installing / installError — 启动时安装(exchange)的状态
const AuthContext = createContext({
  shopDomain: SHOP_DOMAIN,
  getSessionToken: null,
  ensureInstall: null,
  installing: false,
  installError: null
});

export function useAuth() {
  return useContext(AuthContext);
}

// ===== 支持后台(FlexCover 支持面板)模拟登录模式 =====
// 支持人员从管理后台点用户 ID 跳来时,URL 带 ?imp_token=<2h JWT>&shop=…。
// 该令牌是后端签发、冒充该店铺 session token 的 JWT:所有请求像嵌入模式一样
// 带上它,后端从 iss 解析店铺 —— 对每个接口来说,这就是一次商家会话。
// 这里没有 App Bridge,自然也没有 token exchange;令牌过期后所有请求 401,
// 顶栏会提示回管理后台重新生成。
// 令牌进 sessionStorage 后立刻从地址栏洗掉(截图/分享页面链接不会泄露)。

const IMP_SESSION_KEY = 'pp_imp_session';

export function impTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('imp_token');
  if (!token) return null;
  return { token, shop: (params.get('shop') || '').toLowerCase() };
}

export function saveImpSession(token, shop) {
  try {
    sessionStorage.setItem(IMP_SESSION_KEY, JSON.stringify({ token, shop }));
  } catch {
    // 隐私模式等场景拿不到 sessionStorage —— 仅本次渲染生效
  }
}

export function loadImpSession() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(IMP_SESSION_KEY) || 'null');
    if (saved && saved.token) return { shop: '', ...saved };
  } catch {
    // ignore
  }
  return null;
}

export function clearImpSession() {
  try {
    sessionStorage.removeItem(IMP_SESSION_KEY);
  } catch {
    // ignore
  }
}

// 解 JWT payload(不验签 —— 验签由后端做,这里只为展示过期时间)。
function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function impSessionExpiresAt(session) {
  const payload = decodeJwtPayload(session.token);
  const exp = payload && Number(payload.exp);
  return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
}

// 模拟登录模式的 AuthContext:固定令牌 + 固定店铺,ensureInstall 永远是
// no-op(没有 App Bridge id_token 可交换,店铺凭据由商家自己的会话维护)。
export function ImpersonationAuthProvider({ session, children }) {
  const shopDomain = (session.shop || shopDomainFromImpToken(session.token)).toLowerCase();
  const value = useMemo(() => ({
    shopDomain,
    getSessionToken: () => Promise.resolve(session.token),
    ensureInstall: () => Promise.resolve({}),
    installing: false,
    installError: null
  }), [shopDomain, session.token]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// 店铺域名的兜底解析:URL 参数缺失时从令牌 iss(https://<shop>/admin)取。
function shopDomainFromImpToken(token) {
  const payload = decodeJwtPayload(token);
  const iss = (payload && payload.iss) || '';
  return iss.replace(/^https:\/\//, '').replace(/\/admin$/, '');
}

// 本地开发(localhost:3000 直接打开):无 Shopify 上下文,行为与接入
// App Bridge 之前完全一致 —— demo 店铺、无鉴权头、允许灌演示数据。
export function LocalAuthProvider({ children }) {
  const value = useMemo(() => ({
    shopDomain: SHOP_DOMAIN,
    getSessionToken: null,
    ensureInstall: null,
    installing: false,
    installError: null
  }), []);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// 嵌入模式(Shopify admin iframe,App Bridge v4 已由 index.html 的 CDN
// 脚本自动初始化):
//   1. 每次加载先探 GET /api/baseinfo(带 session token;后端只做本地验签 +
//      一次店铺行读取,不走 Shopify 网络)。已安装且未标记卸载 → 直接就绪,
//      跳过 token exchange —— 之前每次刷新都走 /api/auth,JWKS/OAuth 两趟
//      Shopify 往返让打开应用明显变慢。
//   2. 未安装(新店铺 / 数据库重置)或已标记卸载(Shopify 吊销令牌)、以及
//      探测本身失败时,回退完整安装 GET /api/auth?shop=…&id_token=…:验签 +
//      exchange 落库,幂等且自愈。后续运行中凭据再失效(重装、scope 变更)
//      由 useApi 对 "shop is not installed" 的懒重装兜底,不依赖启动流程。
//   3. 之后每个 API 请求带 Authorization: Bearer <session token>(每次请求
//      现取,shopify.idToken() 会自动续期过期的 session token);后端从令牌
//      iss 解析店铺,不需要任何店铺参数。
// 安装也可被后端反向触发:店铺凭据失效被标记重装后,后端对管理端请求返回
// "shop is not installed",useApi 收到后懒调用 ensureInstall 重走 exchange。

const installInFlight = {}; // shopDomain -> Promise,合并并发的安装请求

export function AppBridgeAuthProvider({ children }) {
  const shopify = useAppBridge();
  const params = shopifyUrlParams();
  const shopDomain = (params.shop || '').toLowerCase();
  saveEmbedContext(params.shop, params.host);

  const [install, setInstall] = useState(() => ({
    status: 'installing',
    error: null
  }));

  const runInstall = useCallback(async () => {
    const idToken = await shopify.idToken();
    const res = await fetch(
      `${API_BASE}/api/auth?shop=${encodeURIComponent(shopDomain)}&id_token=${encodeURIComponent(idToken)}`
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `install failed (${res.status})`);
    return data;
  }, [shopify, shopDomain]);

  // 启动探测:已安装返回 true。任何失败(网络错误、旧后端没有该接口、
  // 非法令牌的 401)都不作为启动错误抛出 —— 回退完整安装流程处理,那里
  // 才有针对 id_token / 凭据配置的准确报错。
  const probeInstalled = useCallback(async () => {
    const idToken = await shopify.idToken();
    const res = await fetch(`${API_BASE}/api/baseinfo`, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    if (!res.ok) throw new Error(`baseinfo failed (${res.status})`);
    const data = await res.json().catch(() => ({}));
    return Boolean(data.installed);
  }, [shopify]);

  // 每次嵌入加载:先探 baseinfo,已安装则跳过 exchange 直接就绪;否则
  // (未安装、已卸载、探测失败)重走完整 /api/auth。失败给出可操作提示。
  useEffect(() => {
    if (install.status !== 'installing') return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (await probeInstalled()) return;
      } catch {
        // 探测失败不阻塞 —— 交给完整安装流程定论
      }
      await runInstall();
    })()
      .then(() => {
        if (!cancelled) setInstall({ status: 'ready', error: null });
      })
      .catch((err) => {
        if (!cancelled) setInstall({ status: 'error', error: err.message });
      });
    return () => { cancelled = true; };
  }, [install.status, probeInstalled, runInstall]);

  // 幂等的"确保店铺已安装":并发调用合并为一次 /api/auth。失败不写全局
  // 状态(由触发它的请求自行把错误暴露给页面)。
  const ensureInstall = useCallback(() => {
    if (!installInFlight[shopDomain]) {
      installInFlight[shopDomain] = runInstall().finally(() => {
        delete installInFlight[shopDomain];
      });
    }
    return installInFlight[shopDomain];
  }, [runInstall, shopDomain]);

  const value = useMemo(() => ({
    shopDomain,
    getSessionToken: () => shopify.idToken(),
    ensureInstall,
    installing: install.status === 'installing',
    installError: install.status === 'error' ? install.error : null
  }), [shopDomain, ensureInstall, install.status, install.error, shopify]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// 嵌入模式的启动闸门:token exchange 期间显示连接中;失败给出可操作提示
// (最常见原因:后端 SHOPIFY_API_KEY 与 toml 的 client_id 不一致,或后端未运行)。
export function InstallGate({ children }) {
  const { installing, installError } = useAuth();
  const { t } = useI18n();

  if (installError) {
    return (
      <div style={{ maxWidth: 560, margin: '80px auto', textAlign: 'center' }}>
        <h1>{t('install.title')}</h1>
        <p style={{ color: '#616161' }}>{installError}</p>
        <p style={{ color: '#616161' }}>
          {t('install.body')}
        </p>
        <button type="button" onClick={() => window.location.reload()}>{t('install.retry')}</button>
      </div>
    );
  }
  if (installing) {
    return <BrandLoading />;
  }
  return children;
}
